import { useState } from "react";
import { UserCheck, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export function ReviewerDialog({ tenant, open, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("META REVIEW");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!tenant) return null;
  const existing = (tenant.members || []).find((m) => m.email && m.email.toLowerCase() === email.trim().toLowerCase());

  const close = () => { setResult(null); setEmail(""); setName("META REVIEW"); onClose(); };
  const submit = async () => {
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error("Inserisci un'email valida dedicata al revisore"); return; }
    if (existing && !window.confirm(`«${em}» è già revisore di questo tenant. Rigenerare la password? Quella attuale smetterà di funzionare (aggiorna il modulo Meta se la review è aperta).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/demo-tenant/reviewer", { email: em, name: name.trim() || "META REVIEW" });
      setResult(data);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Creazione revisore non riuscita"); } finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(result.password); toast.success("Password copiata: salvala nel password manager"); } catch { toast.error("Copia non riuscita: seleziona e copia manualmente"); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg" data-testid="reviewer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-[#1877F2]" /> Crea utente revisore Meta — «{tenant.name}»</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Crea (o aggiorna) l'utente di test per la Meta App Review: ruolo globale <b>member</b>, ruolo <b>Marketing</b> sul solo tenant demo, nessun accesso ad altri canali, nessun privilegio Super Admin. La password è generata dal server e mostrata <b>una sola volta</b>.</p>
            <div><Label htmlFor="rev-email">Email dedicata al revisore</Label><Input id="rev-email" type="email" placeholder="meta-review@tuodominio.it" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reviewer-email-input" /></div>
            <div><Label htmlFor="rev-name">Nome visualizzato</Label><Input id="rev-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="reviewer-name-input" /></div>
            {existing && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="reviewer-existing-warning">Revisore già presente: procedendo verrà generata una <b>nuova password</b> (ti verrà chiesta conferma).</p>}
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="reviewer-cancel">Annulla</Button>
              <Button onClick={submit} disabled={busy} data-testid="reviewer-submit"><KeyRound className="h-4 w-4 mr-1" />{busy ? "Creazione…" : existing ? "Rigenera password" : "Crea revisore"}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm" data-testid="reviewer-result">
            <p className="text-slate-700">Utente <b>{result.email}</b> · ruolo <b>{result.role}</b> · tenant <b>{result.tenant_id}</b>.</p>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
              <p className="text-xs text-emerald-900 font-semibold">Password (visibile solo ora — alla chiusura non sarà più recuperabile):</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-white border border-emerald-200 px-2 py-1.5 text-sm select-all" data-testid="reviewer-password">{result.password}</code>
                <Button size="sm" variant="outline" onClick={copy} data-testid="reviewer-copy"><Copy className="h-4 w-4 mr-1" />Copia</Button>
              </div>
              <p className="text-xs text-emerald-900">Salvala nel password manager e inseriscila solo nel campo «Test credentials» del modulo Meta. Nessuna scadenza; lockout 15 min dopo 5 tentativi errati. Per rigenerarla, ripeti questa funzione.</p>
            </div>
            <DialogFooter><Button onClick={close} data-testid="reviewer-close">Ho salvato la password, chiudi</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
